export const COMPRESSIONS = [
    { block: 'DIAMOND_BLOCK', component: 'DIAMOND', ratio: 9 },
    { block: 'IRON_BLOCK', component: 'IRON_INGOT', ratio: 9 },
    { block: 'GOLD_BLOCK', component: 'GOLD_INGOT', ratio: 9 },
    { block: 'EMERALD_BLOCK', component: 'EMERALD', ratio: 9 },
    { block: 'REDSTONE_BLOCK', component: 'REDSTONE', ratio: 9 },
    { block: 'COAL_BLOCK', component: 'COAL', ratio: 9 },
    { block: 'LAPIS_BLOCK', component: 'LAPIS_LAZULI', ratio: 9 },
    { block: 'NETHERITE_BLOCK', component: 'NETHERITE_INGOT', ratio: 9 },
    { block: 'COPPER_BLOCK', component: 'COPPER_INGOT', ratio: 9 },
    { block: 'SLIME_BLOCK', component: 'SLIME_BALL', ratio: 9 },
    { block: 'BONE_BLOCK', component: 'BONE_MEAL', ratio: 9 },
    { block: 'HAY_BLOCK', component: 'WHEAT', ratio: 9 },
    { block: 'DRIED_KELP_BLOCK', component: 'DRIED_KELP', ratio: 9 },
    { block: 'AMETHYST_BLOCK', component: 'AMETHYST_SHARD', ratio: 4 },
    { block: 'QUARTZ_BLOCK', component: 'QUARTZ', ratio: 4 },
    { block: 'IRON_INGOT', component: 'IRON_NUGGET', ratio: 9 },
    { block: 'GOLD_INGOT', component: 'GOLD_NUGGET', ratio: 9 },
];
const BY_BLOCK = new Map(COMPRESSIONS.map((c) => [c.block, c]));
const BY_COMPONENT = new Map(COMPRESSIONS.map((c) => [c.component, c]));
export function compressionOf(material) {
    return BY_BLOCK.get(material) ?? null;
}
export function expansionOf(material) {
    return BY_COMPONENT.get(material) ?? null;
}
/**
 * How far a pair of prices sits from craft parity, as a ratio.
 *
 * 1 means exact parity. Below 1 the block is cheaper than its parts - buy
 * blocks, uncraft, sell parts. Above 1 the reverse. Some spread is normal:
 * crafting costs a moment and bulk usually carries a small discount.
 */
export function parityRatio(blockPrice, componentPrice, ratio) {
    const parts = componentPrice * ratio;
    if (parts <= 0)
        return 1;
    return blockPrice / parts;
}
/**
 * Beyond this the two prices are not describing the same market.
 *
 * Deliberately loose - a 25% spread either way is ordinary for bulk - so that
 * only a genuine contradiction trips it, not normal variation.
 */
export const PARITY_TOLERANCE = 0.25;
export function parityBroken(ratioValue) {
    return ratioValue < 1 - PARITY_TOLERANCE || ratioValue > 1 + PARITY_TOLERANCE;
}
/**
 * The nearest form of the same product that has a usable price.
 *
 * Walks the reversible recipes in both directions: a block can be priced from
 * its component and a component from its block, because the craft goes both
 * ways and arbitrage keeps the two in line. Iron nuggets can therefore be
 * priced from iron blocks two steps away - nugget to ingot to block - which is
 * how most of the long tail gets a number at all.
 *
 * Breadth-first, so the answer is always the fewest steps from real evidence.
 * Each hop multiplies the error, so the search stops well before the
 * arithmetic outruns the confidence.
 */
export function findRelatedForm(material, hasPrice, maxHops = 2) {
    const seen = new Set([material]);
    let frontier = [
        { material, multiplier: 1, hops: 0 },
    ];
    for (let depth = 0; depth < maxHops; depth += 1) {
        const next = [];
        for (const node of frontier) {
            // Upward: this is a component of something bigger.
            const up = expansionOf(node.material);
            if (up && !seen.has(up.block)) {
                seen.add(up.block);
                // One block is `ratio` of this, so its price divided by the ratio
                // gives one of these.
                const step = { material: up.block, multiplier: node.multiplier / up.ratio, hops: node.hops + 1 };
                if (hasPrice(up.block))
                    return { source: up.block, multiplier: step.multiplier, hops: step.hops };
                next.push(step);
            }
            // Downward: this is made of something smaller.
            const down = compressionOf(node.material);
            if (down && !seen.has(down.component)) {
                seen.add(down.component);
                const step = {
                    material: down.component,
                    multiplier: node.multiplier * down.ratio,
                    hops: node.hops + 1,
                };
                if (hasPrice(down.component)) {
                    return { source: down.component, multiplier: step.multiplier, hops: step.hops };
                }
                next.push(step);
            }
        }
        frontier = next;
        if (frontier.length === 0)
            break;
    }
    return null;
}
//# sourceMappingURL=crafting.js.map